import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Save, Bell, Eye, User, ChevronRight, ChevronDown, Lock, Mail, AtSign,
  Download, Trash2, Shield, ExternalLink, Check, X, Loader2,
} from 'lucide-react';
import {
  getSettings, updateSettings, getAccountInfo, changePassword, changeEmail,
  changeUsername, checkUsernameAvailability, deleteAccount, exportUserData,
  getDataUsageStats, getNotifications,
} from '../lib/api-client';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { toast } from '../components/ui/toaster';
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import PostDetailNavbar from '../components/desktop/PostDetailNavbar';
import LoadingScreen from '../components/desktop/LoadingScreen';

// ─── Types ───────────────────────────────────────────────────────────────────

type NotificationSettings = {
  followEnabled: boolean;
  likeEnabled: boolean;
  replyEnabled: boolean;
  communityReplyEnabled: boolean;
  messageEnabled: boolean;
  emailNotifications: boolean;
};

type ProfileVisibility = 'public' | 'friends_of_friends' | 'mutual_followers' | 'followers' | 'private';

type VisibilitySettings = {
  profileVisibility: ProfileVisibility;
  showEmail: boolean;
  showFollowers: boolean;
  postDefaultVisibility: 'public' | 'followers' | 'private';
  whoCanMessage: 'everyone' | 'friends' | 'nobody';
  searchVisible: boolean;
  showActivityStatus: boolean;
};

type SectionId = 'notifications' | 'visibility' | 'account' | 'data';

type AccountInfo = {
  email: string;
  username: string;
  createdAt: string;
  postCount: number;
};

type DataStats = {
  postCount: number;
  commentCount: number;
  communityCount: number;
  messageCount: number;
  followerCount: number;
  followingCount: number;
  accountAge: string;
};

// ─── Toggle Component ────────────────────────────────────────────────────────

function Toggle({ value, onChange }: { value: boolean; onChange: () => void }) {
  return (
    <button
      onClick={onChange}
      className={`relative w-14 h-8 rounded-full transition-all flex-shrink-0 ${
        value ? 'bg-emerald-500' : 'bg-white/20'
      }`}
    >
      <div
        className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
          value ? 'translate-x-6' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// ─── OptionSelector Component ────────────────────────────────────────────────

function OptionSelector<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string; description: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="space-y-2">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`w-full text-left p-3 rounded-lg transition-all ${
            value === option.value
              ? 'bg-emerald-500 text-white'
              : 'bg-white/5 text-white/80 hover:bg-white/10'
          }`}
        >
          <div className="font-medium">{option.label}</div>
          <div className="text-sm opacity-80">{option.description}</div>
        </button>
      ))}
    </div>
  );
}

// ─── FormInput Component ─────────────────────────────────────────────────────

function FormInput({
  type = 'text',
  placeholder,
  value,
  onChange,
  suffix,
}: {
  type?: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  suffix?: React.ReactNode;
}) {
  return (
    <div className="relative">
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-emerald-500 outline-none transition-colors"
      />
      {suffix && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">{suffix}</div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  // Navbar state
  const [navNotifications, setNavNotifications] = useState<any[]>([]);
  const [navUnreadCount, setNavUnreadCount] = useState(0);
  const [showNavNotifications, setShowNavNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'following' | 'friends'>('all');

  // Section navigation
  const [activeSection, setActiveSection] = useState<SectionId>('notifications');

  // Settings state
  const [notifications, setNotifications] = useState<NotificationSettings>({
    followEnabled: true,
    likeEnabled: true,
    replyEnabled: true,
    communityReplyEnabled: true,
    messageEnabled: true,
    emailNotifications: false,
  });
  const [visibility, setVisibility] = useState<VisibilitySettings>({
    profileVisibility: 'public',
    showEmail: false,
    showFollowers: true,
    postDefaultVisibility: 'public',
    whoCanMessage: 'everyone',
    searchVisible: true,
    showActivityStatus: true,
  });

  // Loading/saving state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Account info
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);

  // Account forms
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [showUsernameForm, setShowUsernameForm] = useState(false);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Email form
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');

  // Username form
  const [newUsername, setNewUsername] = useState('');
  const [usernamePassword, setUsernamePassword] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const usernameDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Delete account modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Data stats
  const [dataStats, setDataStats] = useState<DataStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // ─── Load settings ───────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const [settingsData, notifData] = await Promise.all([
          getSettings(),
          getNotifications(20).catch(() => ({ notifications: [], unreadCount: 0 })),
        ]);
        if (settingsData.notifications) setNotifications(prev => ({ ...prev, ...settingsData.notifications }));
        if (settingsData.visibility) setVisibility(prev => ({ ...prev, ...settingsData.visibility }));
        setNavNotifications(notifData.notifications || []);
        setNavUnreadCount(notifData.unreadCount || 0);
      } catch {
        setError('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Load account info when account section is active
  useEffect(() => {
    if (activeSection === 'account' && !accountInfo) {
      getAccountInfo().then(setAccountInfo).catch(() => {});
    }
  }, [activeSection, accountInfo]);

  // Load data stats when data section is active
  useEffect(() => {
    if (activeSection === 'data' && !dataStats) {
      setLoadingStats(true);
      getDataUsageStats().then(setDataStats).catch(() => {}).finally(() => setLoadingStats(false));
    }
  }, [activeSection, dataStats]);

  // ─── Debounced username check ────────────────────────────────────────────

  useEffect(() => {
    if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    if (!newUsername || newUsername.length < 3) {
      setUsernameAvailable(null);
      return;
    }
    setCheckingUsername(true);
    usernameDebounceRef.current = setTimeout(async () => {
      try {
        const result = await checkUsernameAvailability(newUsername);
        setUsernameAvailable(result.available);
      } catch {
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    }, 300);
    return () => {
      if (usernameDebounceRef.current) clearTimeout(usernameDebounceRef.current);
    };
  }, [newUsername]);

  // ─── Navbar handlers ──────────────────────────────────────────────────────

  const handleLogout = useCallback(() => {
    logout?.();
    navigate('/login');
  }, [logout, navigate]);

  const handleNotificationClick = useCallback((notification: any) => {
    setShowNavNotifications(false);
    if (notification.type === 'FOLLOW' || notification.type === 'NEW_FRIEND') {
      navigate(`/u/${notification.actorUsername || notification.actorName}`);
    } else if (notification.postId) {
      navigate(`/community/${notification.communitySlug || 'post'}/${notification.postSlug || notification.postId}`);
    }
  }, [navigate]);

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleNotificationToggle = (key: keyof NotificationSettings) => {
    setNotifications(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleVisibilityChange = <K extends keyof VisibilitySettings>(key: K, value: VisibilitySettings[K]) => {
    setVisibility(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateSettings({ notifications, visibility });
      setSaved(true);
      toast('Settings saved');
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormError(null);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setNewEmail('');
    setEmailPassword('');
    setNewUsername('');
    setUsernamePassword('');
    setUsernameAvailable(null);
  };

  const handleChangePassword = async () => {
    setFormError(null);
    if (newPassword.length < 6) { setFormError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setFormError('Passwords do not match'); return; }
    setFormSaving(true);
    try {
      await changePassword({ currentPassword, newPassword, confirmPassword });
      toast('Password changed successfully');
      resetForm();
      setShowPasswordForm(false);
    } catch (err: any) {
      setFormError(err.message || 'Failed to change password');
    } finally {
      setFormSaving(false);
    }
  };

  const handleChangeEmail = async () => {
    setFormError(null);
    if (!newEmail.includes('@')) { setFormError('Please enter a valid email'); return; }
    setFormSaving(true);
    try {
      const result = await changeEmail({ newEmail, password: emailPassword });
      toast('Email changed successfully');
      setAccountInfo(prev => prev ? { ...prev, email: result.email } : prev);
      resetForm();
      setShowEmailForm(false);
    } catch (err: any) {
      setFormError(err.message || 'Failed to change email');
    } finally {
      setFormSaving(false);
    }
  };

  const handleChangeUsername = async () => {
    setFormError(null);
    if (newUsername.length < 3) { setFormError('Username must be at least 3 characters'); return; }
    if (usernameAvailable === false) { setFormError('Username is not available'); return; }
    setFormSaving(true);
    try {
      const result = await changeUsername({ newUsername, password: usernamePassword });
      toast('Username changed successfully');
      setAccountInfo(prev => prev ? { ...prev, username: result.username } : prev);
      resetForm();
      setShowUsernameForm(false);
    } catch (err: any) {
      setFormError(err.message || 'Failed to change username');
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setFormError(null);
    setDeleting(true);
    try {
      await deleteAccount({ password: deletePassword, confirmation: deleteConfirmation as 'DELETE MY ACCOUNT' });
      toast('Account deleted');
      setShowDeleteModal(false);
      logout?.();
      navigate('/login');
    } catch (err: any) {
      setFormError(err.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  const handleExportData = async () => {
    try {
      const blob = await exportUserData();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `earthsocial-data-export-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast('Data export downloaded');
    } catch {
      toast('Failed to export data');
    }
  };

  // ─── Section definitions ─────────────────────────────────────────────────

  const sections: { id: SectionId; name: string; icon: typeof Bell }[] = [
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'visibility', name: 'Privacy & Visibility', icon: Eye },
    { id: 'account', name: 'Account & Security', icon: Lock },
    { id: 'data', name: 'Data & Legal', icon: Shield },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen">
      <PostDetailNavbar
        onBack={() => navigate(-1)}
        onLogout={handleLogout}
        currentUser={user}
        notifications={navNotifications}
        unreadCount={navUnreadCount}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        theme={theme}
        showNotifications={showNavNotifications}
        setShowNotifications={setShowNavNotifications}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        onNotificationClick={handleNotificationClick}
        hideTabs
      />

      <div className="p-4 md:p-8 pt-8 md:pt-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6 text-center">
          <h1 className="text-3xl font-bold text-white mb-2">Settings</h1>
          <p className="text-white/80">Manage your account preferences and privacy settings</p>
        </div>

        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 mb-6">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="md:w-64 flex-shrink-0 space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => { setActiveSection(section.id); setFormError(null); }}
                className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                  activeSection === section.id
                    ? 'bg-white/20 text-white'
                    : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <section.icon size={20} />
                  <span className="font-medium">{section.name}</span>
                </div>
                <ChevronRight size={16} />
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 bg-white/10 backdrop-blur-sm rounded-2xl p-6">

            {/* ═══ NOTIFICATIONS ═══ */}
            {activeSection === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Notification Preferences</h2>
                  <p className="text-white/70">Choose what notifications you want to receive</p>
                </div>
                <div className="space-y-4">
                  {([
                    { key: 'followEnabled' as const, label: 'New Followers', description: 'Get notified when someone follows you' },
                    { key: 'likeEnabled' as const, label: 'Likes', description: 'Get notified when someone likes your posts' },
                    { key: 'replyEnabled' as const, label: 'Post Replies', description: 'Get notified when someone replies to your posts' },
                    { key: 'communityReplyEnabled' as const, label: 'Community Replies', description: 'Get notified about replies in communities' },
                    { key: 'messageEnabled' as const, label: 'Direct Messages', description: 'Get notified when you receive new messages' },
                    { key: 'emailNotifications' as const, label: 'Email Notifications', description: 'Receive notification emails to your inbox' },
                  ]).map((item) => (
                    <div
                      key={item.key}
                      className="flex items-start justify-between p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all"
                    >
                      <div className="flex-1 mr-4">
                        <h3 className="text-white font-medium mb-1">{item.label}</h3>
                        <p className="text-white/60 text-sm">{item.description}</p>
                      </div>
                      <Toggle
                        value={notifications[item.key]}
                        onChange={() => handleNotificationToggle(item.key)}
                      />
                    </div>
                  ))}
                </div>

                {/* Save button for notifications */}
                <div className="pt-6 border-t border-white/10">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`w-full md:w-auto px-8 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                      saved ? 'bg-emerald-500 text-white' : 'bg-white/20 hover:bg-white/30 text-white'
                    } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Save size={20} />
                    {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}

            {/* ═══ PRIVACY & VISIBILITY ═══ */}
            {activeSection === 'visibility' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Privacy & Visibility</h2>
                  <p className="text-white/70">Control who can see your profile and content</p>
                </div>

                {/* Profile Visibility */}
                <div className="p-4 bg-white/5 rounded-xl">
                  <h3 className="text-white font-medium mb-3">Profile Visibility</h3>
                  <OptionSelector
                    options={[
                      { value: 'public' as const, label: 'Public', description: 'Anyone can see your profile' },
                      { value: 'friends_of_friends' as const, label: 'Friends of Friends', description: 'Extended network only' },
                      { value: 'mutual_followers' as const, label: 'Mutual Followers', description: 'People you both follow' },
                      { value: 'followers' as const, label: 'Followers Only', description: 'Only your followers' },
                      { value: 'private' as const, label: 'Private', description: 'Only you can see' },
                    ]}
                    value={visibility.profileVisibility}
                    onChange={(v) => handleVisibilityChange('profileVisibility', v)}
                  />
                </div>

                {/* Post Default Visibility */}
                <div className="p-4 bg-white/5 rounded-xl">
                  <h3 className="text-white font-medium mb-3">Default Post Visibility</h3>
                  <OptionSelector
                    options={[
                      { value: 'public' as const, label: 'Public', description: 'Anyone can see your posts' },
                      { value: 'followers' as const, label: 'Followers Only', description: 'Only your followers can see' },
                      { value: 'private' as const, label: 'Private', description: 'Only you can see' },
                    ]}
                    value={visibility.postDefaultVisibility}
                    onChange={(v) => handleVisibilityChange('postDefaultVisibility', v)}
                  />
                </div>

                {/* Who Can Message */}
                <div className="p-4 bg-white/5 rounded-xl">
                  <h3 className="text-white font-medium mb-3">Who Can Message You</h3>
                  <OptionSelector
                    options={[
                      { value: 'everyone' as const, label: 'Everyone', description: 'Anyone can send you messages' },
                      { value: 'friends' as const, label: 'Friends Only', description: 'Only mutual followers can message you' },
                      { value: 'nobody' as const, label: 'Nobody', description: 'Disable incoming messages' },
                    ]}
                    value={visibility.whoCanMessage}
                    onChange={(v) => handleVisibilityChange('whoCanMessage', v)}
                  />
                </div>

                {/* Toggle settings */}
                <div className="p-4 bg-white/5 rounded-xl space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 mr-4">
                      <h3 className="text-white font-medium mb-1">Show Email Address</h3>
                      <p className="text-white/60 text-sm">Let others see your email on your profile</p>
                    </div>
                    <Toggle value={visibility.showEmail} onChange={() => handleVisibilityChange('showEmail', !visibility.showEmail)} />
                  </div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 mr-4">
                      <h3 className="text-white font-medium mb-1">Show Follower Count</h3>
                      <p className="text-white/60 text-sm">Display your follower count publicly</p>
                    </div>
                    <Toggle value={visibility.showFollowers} onChange={() => handleVisibilityChange('showFollowers', !visibility.showFollowers)} />
                  </div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 mr-4">
                      <h3 className="text-white font-medium mb-1">Appear in Searches</h3>
                      <p className="text-white/60 text-sm">Allow others to find you through search</p>
                    </div>
                    <Toggle value={visibility.searchVisible} onChange={() => handleVisibilityChange('searchVisible', !visibility.searchVisible)} />
                  </div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1 mr-4">
                      <h3 className="text-white font-medium mb-1">Show Activity Status</h3>
                      <p className="text-white/60 text-sm">Let others see when you're online</p>
                    </div>
                    <Toggle value={visibility.showActivityStatus} onChange={() => handleVisibilityChange('showActivityStatus', !visibility.showActivityStatus)} />
                  </div>
                </div>

                {/* Save button for visibility */}
                <div className="pt-6 border-t border-white/10">
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className={`w-full md:w-auto px-8 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                      saved ? 'bg-emerald-500 text-white' : 'bg-white/20 hover:bg-white/30 text-white'
                    } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Save size={20} />
                    {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
                  </button>
                </div>
              </div>
            )}

            {/* ═══ ACCOUNT & SECURITY ═══ */}
            {activeSection === 'account' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Account & Security</h2>
                  <p className="text-white/70">Manage your account credentials and security</p>
                </div>

                {/* Account info display */}
                {accountInfo && (
                  <div className="p-4 bg-white/5 rounded-xl space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Email</span>
                      <span className="text-white">{accountInfo.email}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Username</span>
                      <span className="text-white">@{accountInfo.username}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Member since</span>
                      <span className="text-white">{new Date(accountInfo.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                )}

                {formError && activeSection === 'account' && (
                  <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-3">
                    <p className="text-red-200 text-sm">{formError}</p>
                  </div>
                )}

                {/* Change Password */}
                <div className="bg-white/5 rounded-xl overflow-hidden">
                  <button
                    onClick={() => { setShowPasswordForm(!showPasswordForm); setFormError(null); resetForm(); }}
                    className="w-full p-4 hover:bg-white/5 transition-all text-left flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <Lock size={20} className="text-white/60" />
                      <div>
                        <h3 className="text-white font-medium">Change Password</h3>
                        <p className="text-white/60 text-sm">Update your account password</p>
                      </div>
                    </div>
                    <ChevronDown size={20} className={`text-white/60 transition-transform ${showPasswordForm ? 'rotate-180' : ''}`} />
                  </button>
                  {showPasswordForm && (
                    <div className="px-4 pb-4 space-y-3">
                      <FormInput type="password" placeholder="Current password" value={currentPassword} onChange={setCurrentPassword} />
                      <FormInput type="password" placeholder="New password (min 6 characters)" value={newPassword} onChange={setNewPassword} />
                      <FormInput type="password" placeholder="Confirm new password" value={confirmPassword} onChange={setConfirmPassword} />
                      <button
                        onClick={handleChangePassword}
                        disabled={formSaving || !currentPassword || !newPassword || !confirmPassword}
                        className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        {formSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        {formSaving ? 'Changing...' : 'Change Password'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Change Email */}
                <div className="bg-white/5 rounded-xl overflow-hidden">
                  <button
                    onClick={() => { setShowEmailForm(!showEmailForm); setFormError(null); resetForm(); }}
                    className="w-full p-4 hover:bg-white/5 transition-all text-left flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <Mail size={20} className="text-white/60" />
                      <div>
                        <h3 className="text-white font-medium">Change Email</h3>
                        <p className="text-white/60 text-sm">
                          {accountInfo ? `Current: ${accountInfo.email}` : 'Update your email address'}
                        </p>
                      </div>
                    </div>
                    <ChevronDown size={20} className={`text-white/60 transition-transform ${showEmailForm ? 'rotate-180' : ''}`} />
                  </button>
                  {showEmailForm && (
                    <div className="px-4 pb-4 space-y-3">
                      <FormInput type="email" placeholder="New email address" value={newEmail} onChange={setNewEmail} />
                      <FormInput type="password" placeholder="Current password (for verification)" value={emailPassword} onChange={setEmailPassword} />
                      <button
                        onClick={handleChangeEmail}
                        disabled={formSaving || !newEmail || !emailPassword}
                        className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        {formSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        {formSaving ? 'Changing...' : 'Change Email'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Change Username */}
                <div className="bg-white/5 rounded-xl overflow-hidden">
                  <button
                    onClick={() => { setShowUsernameForm(!showUsernameForm); setFormError(null); resetForm(); }}
                    className="w-full p-4 hover:bg-white/5 transition-all text-left flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <AtSign size={20} className="text-white/60" />
                      <div>
                        <h3 className="text-white font-medium">Change Username</h3>
                        <p className="text-white/60 text-sm">
                          {accountInfo ? `Current: @${accountInfo.username}` : 'Update your username'}
                        </p>
                      </div>
                    </div>
                    <ChevronDown size={20} className={`text-white/60 transition-transform ${showUsernameForm ? 'rotate-180' : ''}`} />
                  </button>
                  {showUsernameForm && (
                    <div className="px-4 pb-4 space-y-3">
                      <FormInput
                        placeholder="New username"
                        value={newUsername}
                        onChange={setNewUsername}
                        suffix={
                          newUsername.length >= 3 ? (
                            checkingUsername ? (
                              <Loader2 size={16} className="animate-spin text-white/40" />
                            ) : usernameAvailable === true ? (
                              <Check size={16} className="text-emerald-400" />
                            ) : usernameAvailable === false ? (
                              <X size={16} className="text-red-400" />
                            ) : null
                          ) : null
                        }
                      />
                      {newUsername.length >= 3 && usernameAvailable === false && (
                        <p className="text-red-300 text-sm">Username is already taken</p>
                      )}
                      {newUsername.length >= 3 && usernameAvailable === true && (
                        <p className="text-emerald-300 text-sm">Username is available</p>
                      )}
                      <FormInput type="password" placeholder="Current password (for verification)" value={usernamePassword} onChange={setUsernamePassword} />
                      <button
                        onClick={handleChangeUsername}
                        disabled={formSaving || !newUsername || !usernamePassword || usernameAvailable !== true}
                        className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                      >
                        {formSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                        {formSaving ? 'Changing...' : 'Change Username'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Delete Account */}
                <button
                  onClick={() => { setShowDeleteModal(true); setFormError(null); setDeletePassword(''); setDeleteConfirmation(''); }}
                  className="w-full p-4 bg-red-500/20 rounded-xl hover:bg-red-500/30 transition-all text-left"
                >
                  <div className="flex items-center gap-3">
                    <Trash2 size={20} className="text-red-300" />
                    <div>
                      <h3 className="text-red-300 font-medium">Delete Account</h3>
                      <p className="text-red-300/70 text-sm">Permanently delete your account and all data</p>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* ═══ DATA & LEGAL ═══ */}
            {activeSection === 'data' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">Data & Legal</h2>
                  <p className="text-white/70">Your data usage and legal information</p>
                </div>

                {/* Data Stats */}
                {loadingStats ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 size={24} className="animate-spin text-white/40" />
                  </div>
                ) : dataStats && (
                  <div className="p-4 bg-white/5 rounded-xl">
                    <h3 className="text-white font-medium mb-4">Your Data</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      {[
                        { label: 'Posts', value: dataStats.postCount },
                        { label: 'Comments', value: dataStats.commentCount },
                        { label: 'Communities', value: dataStats.communityCount },
                        { label: 'Messages', value: dataStats.messageCount },
                        { label: 'Followers', value: dataStats.followerCount },
                        { label: 'Following', value: dataStats.followingCount },
                      ].map((stat) => (
                        <div key={stat.label} className="bg-white/5 rounded-lg p-3 text-center">
                          <div className="text-2xl font-bold text-white">{stat.value}</div>
                          <div className="text-white/60 text-sm">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-sm text-white/60 text-center">
                      Account age: {dataStats.accountAge}
                    </div>
                  </div>
                )}

                {/* Export Data */}
                <button
                  onClick={handleExportData}
                  className="w-full p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-all text-left flex items-center gap-3"
                >
                  <Download size={20} className="text-white/60" />
                  <div>
                    <h3 className="text-white font-medium">Download Your Data</h3>
                    <p className="text-white/60 text-sm">Export all your account data as JSON</p>
                  </div>
                </button>

                {/* Legal Links */}
                <div className="p-4 bg-white/5 rounded-xl space-y-1">
                  <h3 className="text-white font-medium mb-3">Legal</h3>
                  <Link
                    to="/terms"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-all"
                  >
                    <span className="text-white">Terms of Service</span>
                    <ExternalLink size={16} className="text-white/40" />
                  </Link>
                  <Link
                    to="/privacy"
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-white/5 transition-all"
                  >
                    <span className="text-white">Privacy Policy</span>
                    <ExternalLink size={16} className="text-white/40" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>

      {/* ═══ DELETE ACCOUNT MODAL ═══ */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent
          className="border-red-500/30"
          style={{ background: 'rgba(30, 10, 10, 0.95)', borderColor: 'rgba(239, 68, 68, 0.3)' }}
        >
          <DialogHeader>
            <DialogTitle className="text-red-300 text-xl">Delete Account</DialogTitle>
            <DialogDescription className="text-red-200/70">
              This action is permanent and cannot be undone. All your data, posts, messages, and connections will be permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {formError && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
                <p className="text-red-200 text-sm">{formError}</p>
              </div>
            )}
            <div>
              <label className="text-red-200/80 text-sm mb-1.5 block">Enter your password</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Password"
                className="w-full bg-white/10 border border-red-500/30 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-red-500 outline-none"
              />
            </div>
            <div>
              <label className="text-red-200/80 text-sm mb-1.5 block">
                Type <span className="font-bold text-red-300">DELETE MY ACCOUNT</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                placeholder="DELETE MY ACCOUNT"
                className="w-full bg-white/10 border border-red-500/30 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:border-red-500 outline-none"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowDeleteModal(false)}
              className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirmation !== 'DELETE MY ACCOUNT' || !deletePassword}
              className="px-6 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors flex items-center gap-2"
            >
              {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              {deleting ? 'Deleting...' : 'Delete Account'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
